import type { UserRole } from '~/enums'
import { Normalization } from '~/utils/normalization'
import { isSafeRedirectPath } from '~/utils/isSafeRedirectPath'
import { isSafeStoreSlug } from '~/utils/isSafeStoreSlug'
import { parseSafeRedirectTarget } from '~/utils/parseSafeRedirectTarget'
import { resolveRoleHomeDestination } from '~/utils/resolveRoleHomeDestination'

// A query array (e.g. ?returnUrl[]=a&returnUrl[]=b) is rejected outright, not coerced to a string -- only a single scalar value is ever a real redirect target.
const trimmedQueryValue = (value: unknown): string | null =>
  typeof value === 'string' ? Normalization.trimmedStringValue(value) : null

/**
 * Shared post-login/post-registration redirect target: checkout flow, then an
 * explicit return URL (`?redirect=` from Sanctum, `?returnUrl=` from route
 * protection, or a stored `authReturnUrl`), then a role-based default.
 */
export const resolvePostAuthDestination = (
  route: { query: Record<string, unknown> },
  roles: readonly UserRole[]
): string | { path: string; query: Record<string, string> } => {
  const checkoutStoreSlug = Normalization.trimmedStringValue(
    sessionStorage.getItem('checkoutStoreSlug')
  )
  if (isSafeStoreSlug(checkoutStoreSlug)) {
    sessionStorage.removeItem('checkoutStoreSlug')
    return `/checkout/${checkoutStoreSlug}`
  }

  const returnUrl =
    trimmedQueryValue(route.query['redirect']) ??
    trimmedQueryValue(route.query['returnUrl']) ??
    Normalization.trimmedStringValue(sessionStorage.getItem('authReturnUrl'))
  if (isSafeRedirectPath(returnUrl)) {
    sessionStorage.removeItem('authReturnUrl')
    return parseSafeRedirectTarget(returnUrl)
  }

  return resolveRoleHomeDestination(roles)
}
